import { Component, type ErrorInfo, type ReactNode } from "react";
import { eventBus } from "../services/eventBus";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/** Root boundary; the one place a class component is required. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    eventBus.publish("error", { error, context: `ErrorBoundary (${info.componentStack ?? "unknown"})` });
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="mb-2 text-2xl font-bold text-brand-navy">Something went wrong</h1>
          <p className="mb-4 text-gray-600">{this.state.error.message}</p>
          <button type="button" onClick={() => this.setState({ error: null })} className="min-h-[44px] cursor-pointer rounded-lg bg-brand-green px-6 py-2 font-medium text-white hover:bg-brand-green-hover focus:outline-none focus:ring-2 focus:ring-brand-green-50">
            Try Again
          </button>
        </div>
      </div>
    );
  }
}
